import { ReactElement } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import { Warning as WarningIcon } from "@mui/icons-material";

export interface ProjectSwitchConfirmationModalProps {
  open: boolean;
  currentProjectName: string;
  targetProjectName: string;
  hasAnalysisProgress?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

const ProjectSwitchConfirmationModal = ({
  open,
  currentProjectName,
  targetProjectName,
  hasAnalysisProgress = true,
  onConfirm,
  onCancel,
}: ProjectSwitchConfirmationModalProps): ReactElement => {
  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "12px",
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" alignItems="center" gap={1.5}>
          <WarningIcon color="warning" sx={{ fontSize: 28 }} />
          <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
            Switch Project?
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {hasAnalysisProgress && (
          <Alert severity="warning" sx={{ mb: 3 }} icon={false}>
            This action will reset your current analysis progress.
          </Alert>
        )}

        <Typography variant="body1" sx={{ mb: 2 }}>
          You are about to switch from <strong>"{currentProjectName}"</strong>{" "}
          to <strong>"{targetProjectName}"</strong>.
        </Typography>

        {hasAnalysisProgress && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              This will reset all your current analysis progress, including:
            </Typography>

            <List dense sx={{ py: 0, px: 1 }}>
              <ListItem sx={{ py: 0.5, px: 0 }}>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <Box
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      backgroundColor: "warning.main",
                    }}
                  />
                </ListItemIcon>
                <ListItemText
                  primary="Selected files"
                  sx={{
                    "& .MuiListItemText-primary": {
                      fontSize: "0.875rem",
                      color: "text.secondary",
                    },
                  }}
                />
              </ListItem>
              <ListItem sx={{ py: 0.5, px: 0 }}>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <Box
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      backgroundColor: "warning.main",
                    }}
                  />
                </ListItemIcon>
                <ListItemText
                  primary="Active workflow steps"
                  sx={{
                    "& .MuiListItemText-primary": {
                      fontSize: "0.875rem",
                      color: "text.secondary",
                    },
                  }}
                />
              </ListItem>
              <ListItem sx={{ py: 0.5, px: 0 }}>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <Box
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      backgroundColor: "warning.main",
                    }}
                  />
                </ListItemIcon>
                <ListItemText
                  primary="Processing results"
                  sx={{
                    "& .MuiListItemText-primary": {
                      fontSize: "0.875rem",
                      color: "text.secondary",
                    },
                  }}
                />
              </ListItem>
              <ListItem sx={{ py: 0.5, px: 0 }}>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <Box
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      backgroundColor: "warning.main",
                    }}
                  />
                </ListItemIcon>
                <ListItemText
                  primary="Analysis state"
                  sx={{
                    "& .MuiListItemText-primary": {
                      fontSize: "0.875rem",
                      color: "text.secondary",
                    },
                  }}
                />
              </ListItem>
            </List>
          </>
        )}

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mt: 2, fontStyle: "italic" }}
        >
          Are you sure you want to continue?
        </Typography>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 2, justifyContent: "space-between" }}>
        <Button
          onClick={onCancel}
          variant="outlined"
          sx={{
            textTransform: "none",
            fontWeight: 500,
            px: 3,
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          color="warning"
          sx={{
            textTransform: "none",
            fontWeight: 500,
            px: 3,
          }}
        >
          Switch Project
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProjectSwitchConfirmationModal;
